/**
 * TenantConfigContext
 *
 * OEM/ホワイトラベル対応のためのテナント設定 Context
 * - 認証前: tenant-config API（public）から brand/theme を取得
 * - 認証後: bootstrap API から全設定を取得
 *
 * Related: OEM-BASE-03
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { useAuth } from "./AuthContext";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface TenantBrand {
  name: string;
  tagline?: string | null;
  logo_url?: string | null;
  favicon_url?: string | null;
  og_image_url?: string | null;
}

export interface TenantTheme {
  primary_color: string;
  secondary_color?: string | null;
  font_family?: string | null;
}

export interface TenantFeatureFlags {
  payment_enabled: boolean;
  line_enabled: boolean;
  vision_api_enabled: boolean;
  human_escalation_enabled: boolean;
  analytics_enabled: boolean;
}

export interface TenantSupport {
  email?: string | null;
  company_name?: string | null;
  help_url?: string | null;
}

export interface TenantLegal {
  terms_url?: string | null;
  privacy_url?: string | null;
}

export interface TenantConfig {
  tenant_key: string;
  brand: TenantBrand;
  theme: TenantTheme;
  domain?: string | null;
  feature_flags?: TenantFeatureFlags;
  support?: TenantSupport;
  legal?: TenantLegal;
}

interface TenantConfigContextValue {
  config: TenantConfig | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Default values
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_BRAND: TenantBrand = {
  name: "JARVIS LMS",
  tagline: "AI搭載学習プラットフォーム",
};

const DEFAULT_THEME: TenantTheme = {
  primary_color: "#3B82F6",
};

const DEFAULT_CONFIG: TenantConfig = {
  tenant_key: "default",
  brand: DEFAULT_BRAND,
  theme: DEFAULT_THEME,
};

// ─────────────────────────────────────────────────────────────────────────────
// Utilities
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Convert hex color to HSL string for shadcn/ui CSS variables
 * @param hex - Hex color string (e.g., "#3B82F6")
 * @returns HSL string (e.g., "217 91% 60%") or null if invalid
 */
function hexToHsl(hex: string): string | null {
  // Remove # if present
  const cleanHex = hex.replace(/^#/, "");

  // Parse hex to RGB
  const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(cleanHex);
  if (!result) return null;

  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  // Return HSL without commas (shadcn format)
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

const TenantConfigContext = createContext<TenantConfigContextValue>({
  config: null,
  loading: true,
  error: null,
  refetch: async () => {},
});

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────

interface TenantConfigProviderProps {
  children: ReactNode;
}

export function TenantConfigProvider({ children }: TenantConfigProviderProps) {
  const { user, loading: authLoading } = useAuth();
  const [config, setConfig] = useState<TenantConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const apiBaseUrl =
    import.meta.env.VITE_API_BASE_URL ||
    (import.meta.env.DEV ? "http://localhost:8000" : "");

  // Fetch public tenant config (no auth required)
  const fetchPublicConfig = useCallback(async () => {
    try {
      const host = window.location.hostname;
      const response = await fetch(
        `${apiBaseUrl}/api/v1/public/tenant-config?host=${encodeURIComponent(host)}`
      );

      if (!response.ok) {
        // Fallback to default if tenant not found
        if (response.status === 404) {
          setConfig(DEFAULT_CONFIG);
          return;
        }
        throw new Error(`Failed to fetch tenant config: ${response.status}`);
      }

      const data = await response.json();
      setConfig(data);
    } catch (err) {
      console.warn("Failed to fetch public tenant config, using defaults:", err);
      setConfig(DEFAULT_CONFIG);
    }
  }, [apiBaseUrl]);

  // Fetch full bootstrap config (auth required)
  const fetchBootstrapConfig = useCallback(async () => {
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;

      if (!token) {
        // No token, fall back to public config
        await fetchPublicConfig();
        return;
      }

      const response = await fetch(`${apiBaseUrl}/api/v1/bootstrap`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch bootstrap config: ${response.status}`);
      }

      const data = await response.json();
      setConfig(data);
    } catch (err) {
      console.warn("Failed to fetch bootstrap config, falling back to public:", err);
      await fetchPublicConfig();
    }
  }, [apiBaseUrl, fetchPublicConfig]);

  // Main fetch logic
  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      if (user) {
        // Authenticated: use bootstrap API
        await fetchBootstrapConfig();
      } else {
        // Not authenticated: use public API
        await fetchPublicConfig();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      setError(message);
      setConfig(DEFAULT_CONFIG);
    } finally {
      setLoading(false);
    }
  }, [user, fetchBootstrapConfig, fetchPublicConfig]);

  // Refetch function for manual refresh
  const refetch = useCallback(async () => {
    await fetchConfig();
  }, [fetchConfig]);

  // Fetch config when auth state changes
  useEffect(() => {
    if (!authLoading) {
      fetchConfig();
    }
  }, [authLoading, user, fetchConfig]);

  // OEM-VISUAL-01: Apply theme colors to CSS variables
  useEffect(() => {
    if (!config?.theme) return;

    const root = document.documentElement;
    const { primary_color, secondary_color } = config.theme;

    // Apply primary color (shadcn/ui uses HSL format)
    if (primary_color) {
      const primaryHsl = hexToHsl(primary_color);
      if (primaryHsl) {
        // shadcn/ui CSS variables expect HSL without 'hsl()' wrapper
        root.style.setProperty("--primary", primaryHsl);
      }
      // Also set hex version for direct use
      root.style.setProperty("--oem-primary-hex", primary_color);
    }

    // Apply secondary color
    if (secondary_color) {
      const secondaryHsl = hexToHsl(secondary_color);
      if (secondaryHsl) {
        root.style.setProperty("--secondary", secondaryHsl);
      }
      root.style.setProperty("--oem-secondary-hex", secondary_color);
    }

    // Log for debugging in non-production
    if (import.meta.env.DEV) {
      console.log("[TenantConfig] Applied theme:", {
        primary_color,
        secondary_color,
        primary_hsl: hexToHsl(primary_color),
      });
    }
  }, [config?.theme]);

  return (
    <TenantConfigContext.Provider value={{ config, loading, error, refetch }}>
      {children}
    </TenantConfigContext.Provider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useTenantConfig(): TenantConfigContextValue {
  const context = useContext(TenantConfigContext);
  if (!context) {
    throw new Error("useTenantConfig must be used within a TenantConfigProvider");
  }
  return context;
}

// Convenience hooks for specific parts of the config
export function useBrand(): TenantBrand {
  const { config } = useTenantConfig();
  return config?.brand ?? DEFAULT_BRAND;
}

export function useTheme(): TenantTheme {
  const { config } = useTenantConfig();
  return config?.theme ?? DEFAULT_THEME;
}

export function useFeatureFlags(): TenantFeatureFlags | undefined {
  const { config } = useTenantConfig();
  return config?.feature_flags;
}

export function useSupport(): TenantSupport | undefined {
  const { config } = useTenantConfig();
  return config?.support;
}

export function useLegal(): TenantLegal | undefined {
  const { config } = useTenantConfig();
  return config?.legal;
}
